import { UserProfile } from '../user-profile';

/**
 * The skills survey's steps. 03-skills-survey section 4, `STEPS` in the design.
 *
 * **A civilian's survey is five steps and never mentions a JST or the ASVAB.**
 * That is acceptance criterion 1, and the important half of it is the
 * numbering: the military-only step is FILTERED OUT of the list, not skipped
 * while still being counted, so "Step 3 of 5" cannot jump from 2 to 4.
 */
export interface SurveyStep {
  key: string;
  /** The name shown beside the number. Section 8: not just dots. */
  name: string;
  /** True when the step only applies to someone with military service. */
  militaryOnly: boolean;
  /** Whether the member has finished it, read off the profile. */
  isComplete: (profile: UserProfile) => boolean;
}

/**
 * The whole set, in order, before filtering.
 *
 * The names avoid military language on the steps everyone sees: "Work
 * experience" rather than "Civilian experience", because to a civilian there is
 * nothing civilian about it, and the contrast only means something to somebody
 * who served.
 */
export const SURVEY_STEPS: SurveyStep[] = [
  {
    key: 'background',
    name: 'Your background',
    militaryOnly: false,
    isComplete: (profile) => !!profile?.profileWorkType?.type
  },
  // CPD-668. Named to match `SKILLS_SURVEY_STEPS`, which this list shadows.
  // "Your goals" is the Goals Report's phrase for a different, later thing,
  // and `userHasPreGoal` below is the giveaway: what this step completes is a
  // PRE-goal, which the report never reads. Walkthrough 09-29 #1 names it
  // "Your goals" again, matching the survey's rail.
  {
    key: 'goals',
    name: 'Your goals',
    militaryOnly: false,
    isComplete: (profile) => !!profile?.userHasPreGoal
  },
  {
    key: 'military',
    name: 'Military experience',
    militaryOnly: true,
    isComplete: (profile) => !!profile?.userMocs?.length
  },
  {
    key: 'work',
    name: 'Work experience',
    militaryOnly: false,
    isComplete: (profile) => !!profile?.userWorkExperiences?.length
  },
  {
    key: 'education',
    name: 'Education',
    militaryOnly: false,
    isComplete: (profile) => !!profile?.userEducations?.length
  },
  {
    key: 'review',
    name: 'Check it and finish',
    militaryOnly: false,
    isComplete: (profile) => !!profile?.isSkillsSurveyComplete
  }
];

/**
 * Whether the military-only step applies.
 *
 * Read off the branch on the profile rather than off profileWorkType, because
 * that enum's first value is FEDERAL_EMPLOYEE_OR_SERVICEMEMBER: it bundles
 * federal civilians in with servicemembers, so it cannot answer this question
 * on its own. A branch is only ever set by somebody who served.
 */
export function isMilitaryMember(profile: UserProfile | null): boolean {
  return !!profile?.militaryBranch?.branchCode;
}

export interface NumberedSurveyStep extends SurveyStep {
  /** 1-based, over the filtered list, so it is what the member is shown. */
  number: number;
  isComplete: SurveyStep['isComplete'];
  complete: boolean;
  /**
   * Whether the member may navigate to it.
   *
   * A step already reached is navigable and one not yet reached is not: section
   * 7, and it is what makes back navigation safe. The step after the last
   * completed one is reachable, because that is the one they are on.
   */
  reachable: boolean;
}

/**
 * The steps this member actually has, numbered over that filtered list.
 *
 * Everything a step indicator needs comes from here, so the filtering and the
 * numbering cannot disagree with each other.
 */
export function applicableSurveySteps(
  profile: UserProfile | null
): NumberedSurveyStep[] {
  const isMilitary = isMilitaryMember(profile);
  const steps = SURVEY_STEPS.filter((step) => !step.militaryOnly || isMilitary);

  const completion = steps.map((step) =>
    profile ? step.isComplete(profile) : false
  );

  // The furthest step reached: everything up to and including the one after the
  // last completed step.
  const lastComplete = completion.lastIndexOf(true);

  return steps.map((step, index) => ({
    ...step,
    number: index + 1,
    complete: completion[index],
    reachable: index <= lastComplete + 1
  }));
}
